<template>
  <div
    class="table-wrapper"
    :class="{
      loading: isLoading,
      refreshing: isRefreshing,
      'has-filters': showFilters,
      'no-data': records && records.length === 0,
    }"
  >
    <table class="data-table">
      <thead class="table-header" @mouseleave="hoverFilterIcon = false">
        <tr v-show="columns.length > 0" ref="tableHeaderRow">
          <th class="_checkbox cell-shrink cell-centered">
            <input
              v-if="records && records.length && selectionMode !== 'radio'"
              type="checkbox"
              :checked="allSelected"
              @click.stop="toggleSelectAll"
            />
          </th>
          <slot name="more-headers-columns" />

          <th v-if="displayDeletedItem" class="cell-shrink cell-centered"></th>
          <th v-if="displayItemStatus" class="cell-shrink cell-centered">
            <span v-tooltip="'Status'" class="icon hoverable-icon">
              <i class="fa-solid fa-signal" />
            </span>
          </th>
          <th v-if="displayDownloadLink" class="cell-shrink cell-centered"></th>
          <th
            v-for="(column, index) in columns"
            :key="`sort-${column.id}${index}`"
            :ref="`head-${column.id}`"
            class="header-cell th--draggable no-select"
            :class="{
              'has-filter-set': filters[column.id],
              'has-filter-active': filters[column.id],
            }"
          >
            <div
              v-if="removingColumnIndex !== index"
              class="header-cell__content"
            >
              <span
                v-if="column.isSortable && column.id === sort.columnId"
                class="header-cell__sort is-active"
              >
                <IconButton
                  :icon="sortIcon(column)"
                  tooltip="Sort"
                  color="primary"
                  size="small"
                  :colorful="true"
                  :show-arrow="false"
                  align="start"
                >
                  <template #dropdown>
                    <a
                      v-for="opt in SORT_OPTIONS"
                      :key="opt.value"
                      class="dd-item"
                      :class="{
                        'is-active': sortState(column) === opt.value,
                      }"
                      @click="applySort(column, opt.value)"
                    >
                      <span class="icon">
                        <i :class="opt.icon" />
                      </span>
                      {{ opt.label }}
                    </a>
                  </template>
                </IconButton>
              </span>

              <span
                v-if="column.category === 'annotation'"
                v-tooltip="'Annotation'"
                class="icon"
              >
                <i class="fa-solid fa-xs fa-tags" />
              </span>
              <span
                class="header-cell__label"
                :class="{ 'is-sorted': column.id === sort.columnId }"
              >
                {{ column.label }}
              </span>

              <HelpIcon
                :label="column.label"
                :text="
                  column.fieldMeta &&
                  (column.fieldMeta.help || column.fieldMeta.description)
                "
              />

              <span class="header-cell__actions">
                <span
                  v-if="column.isSortable && column.id !== sort.columnId"
                  class="header-cell__sort"
                >
                  <IconButton
                    :icon="sortIcon(column)"
                    tooltip="Sort"
                    color="primary"
                    size="small"
                    :show-arrow="false"
                    align="start"
                  >
                    <template #dropdown>
                      <a
                        v-for="opt in SORT_OPTIONS"
                        :key="opt.value"
                        class="dd-item"
                        :class="{
                          'is-active': sortState(column) === opt.value,
                        }"
                        @click="applySort(column, opt.value)"
                      >
                        <span class="icon">
                          <i :class="opt.icon" />
                        </span>
                        {{ opt.label }}
                      </a>
                    </template>
                  </IconButton>
                </span>
                <!-- Drag handle -->
                <IconButton
                  icon="fa-solid fa-arrows-alt-h"
                  tooltip="Drag to reorder columns"
                  size="small"
                  class="th--handle header-cell__btn"
                  :show-arrow="false"
                  @click.stop
                />

                <!-- Move to first -->
                <IconButton
                  v-if="index > 0"
                  icon="fa-solid fa-step-backward"
                  tooltip="Move to first position"
                  color="primary"
                  size="small"
                  class="header-cell__btn"
                  :show-arrow="false"
                  @click.stop="putColumnFirst(index)"
                />

                <!-- Remove filter -->
                <IconButton
                  v-if="filters[column.id]"
                  icon="fa-solid fa-filter"
                  tooltip="Remove column filter"
                  color="primary"
                  size="small"
                  class="header-cell__btn is-active"
                  :colorful="true"
                  :show-arrow="false"
                  @click.stop="unsetFilter(column.id)"
                  @mouseenter="hoverFilterIcon = true"
                />
                <!-- Set filter -->
                <IconButton
                  v-else-if="column.isFilterable !== false && !showFilters"
                  icon="fa-solid fa-filter"
                  tooltip="Set filter"
                  color="primary"
                  size="small"
                  class="header-cell__btn"
                  :show-arrow="false"
                  @click.stop="$emit('toggle-filters')"
                />

                <!-- Remove column -->
                <IconButton
                  icon="fa-solid fa-times"
                  tooltip="Remove column"
                  color="danger"
                  size="small"
                  class="header-cell__btn"
                  :show-arrow="false"
                  @click.stop="removeColumn(index)"
                />
              </span>
            </div>
            <div v-else class="is-flex is-justify-content-center">
              <span class="icon is-medium has-text-danger">
                <i class="fa-solid fa-spinner fa-spin" />
              </span>
            </div>
          </th>
        </tr>
        <tr
          v-show="showFilters || hoverFilterIcon"
          ref="tableFilterRow"
          class="filter-row"
        >
          <th class="_checkbox cell-shrink cell-centered">
            <span
              v-tooltip="
                hasFilters
                  ? 'Remove all column filters'
                  : 'No filter currently set on data'
              "
              class="clear-filters icon fa-stack"
              :class="{ hoverable: hasFilters }"
              tabindex="0"
              @click.stop="handleClearFilters"
            >
              <i
                class="fa-solid fa-filter fa-stack-1x"
                :class="hasFilters ? 'has-text-primary' : 'has-text-gray'"
              />
              <i
                v-if="hasFilters"
                class="fa-solid fa-slash fa-stack-1x fa-flip-vertical has-text-danger icon-slash"
              />
            </span>
          </th>
          <slot name="more-headers-columns" />
          <th v-if="displayDeletedItem" class="cell-shrink cell-centered"></th>
          <th v-if="displayItemStatus" class="cell-shrink cell-centered"></th>
          <th v-if="displayDownloadLink" class="cell-shrink cell-centered"></th>
          <th
            v-for="(column, index) in columns"
            :key="`filt-${column.id}${index}`"
            class="filter-cell"
            :class="{ 'has-filter-active': filters[column.id] }"
          >
            <SearchField
              v-if="
                removingColumnIndex !== index && column.isFilterable !== false
              "
              :disabled="column.isFilterable === false"
              :field-meta="column.fieldMeta"
              :readonly="filters[column.id] && filters[column.id].readonly"
              :value="(filters[column.id] && filters[column.id].value) || ''"
              @change="
                (value) => {
                  if (value === null) unsetFilter(column.id)
                  else setFilter(column.id, value)
                }
              "
            />
          </th>
        </tr>
        <tr v-if="isLoading || isRefreshing" class="progress-row">
          <th colspan="100%" class="progress-cell">
            <ProgressBar />
          </th>
        </tr>
      </thead>
      <tbody
        ref="tableBody"
        :class="{
          'loading-body': isLoading,
          'no-data-body': !isLoading && records && !records.length,
          'is-drag-selecting': dragSelectAnchor !== null,
        }"
        @mouseover="handleColumnHover"
        @mousemove="onBodyMousemove"
        @mouseleave="(clearColumnHover(), onDragSelectEnd())"
        @mouseup="onDragSelectEnd"
      >
        <template v-if="!isLoading && records && records.length">
          <DataTableRow
            v-for="({ selected, object: record }, index) in selectableRecords"
            :key="`row-${record.id.value}`"
            v-slot="{
              isDeleted,
              deletedTooltip,
              status,
              viewable,
              downloadable,
              contentType,
              filesize,
            }"
            :record="record"
            :selected="selected"
            @toggle-select="toggleSelect(index, $event)"
            @mousedown="onRowMousedown(index, $event)"
            @mouseenter="onRowMouseenter(index)"
          >
            <td class="_checkbox cell-shrink cell-centered">
              <input
                :type="selectionMode === 'radio' ? 'radio' : 'checkbox'"
                :checked="selected"
                @click.stop="
                  toggleSelect(
                    index,
                    $event,
                    selectionMode !== 'radio' ? { additive: true } : undefined,
                  )
                "
              />
            </td>
            <slot name="more-data-columns" :record="record" :index="index" />
            <td v-if="displayDeletedItem" class="cell-shrink cell-centered">
              <span
                v-if="isDeleted"
                v-tooltip="deletedTooltip"
                class="icon has-text-danger"
              >
                <i class="far fa-trash-can" />
              </span>
            </td>
            <td
              v-if="displayItemStatus"
              class="icon-column cell-centered cell-status"
            >
              <StatusWidget :status="status" :compact="true" />
            </td>
            <td v-if="displayDownloadLink" class="cell-shrink cell-centered">
              <router-link
                v-if="viewable"
                :to="{
                  name: 'viewFile',
                  query: {
                    id: record.id.value,
                    app: appName,
                    model: modelName,
                    mime: contentType,
                    size: filesize,
                  },
                }"
                target="_blank"
                rel="noopener"
              >
                <IconButton
                  icon="fa-regular fa-eye"
                  size="small"
                  :color="null"
                  tooltip="Preview file"
                />
              </router-link>
              <IconButton
                v-else-if="downloadable"
                icon="fa-solid fa-download"
                size="small"
                tooltip="Download file"
                @click="handleSignedDownload(record.id.value)"
              />
            </td>
            <td
              v-for="(column, colIndex) in columns"
              :key="`cell-${record.id.value}-${column.id}-${colIndex}`"
              :class="{
                'removal-in-progress': removingColumnIndex === colIndex,
                'has-filter-active': filters[column.id],
              }"
            >
              <div
                class="table-cell"
                :class="{
                  'deleted-record-field':
                    isDeleted && (column.id === 'name' || column.id === 'id'),
                }"
              >
                <FieldComponent
                  :field-meta="column.fieldMeta"
                  :data="record[column.id]"
                  :record="record"
                />
              </div>
            </td>
          </DataTableRow>
        </template>
        <template v-else-if="isLoading">
          <tr v-for="index in maxRecords" :key="index" class="skeleton-row">
            <td colspan="100%"></td>
          </tr>
        </template>
      </tbody>
      <tfoot v-if="showFooter" class="table-footer">
        <tr>
          <th class="_checkbox" />
          <th v-if="displayDeletedItem" class="cell-shrink cell-centered"></th>
          <th v-if="displayItemStatus" class="cell-shrink cell-centered"></th>
          <th v-if="displayDownloadLink" class="cell-shrink cell-centered"></th>
          <th
            v-for="(column, index) in columns"
            :key="`label-${column.id}${index}`"
            :class="{ 'has-filter-active': filters[column.id] }"
          >
            <span class="footer-cell">
              <span class="footer-cell__label">
                <IconButton
                  v-if="column.isSortable"
                  :icon="sortIcon(column)"
                  tooltip="Sort"
                  color="primary"
                  size="small"
                  :colorful="column.id === sort.columnId"
                  :show-arrow="false"
                >
                  <template #dropdown>
                    <a
                      v-for="opt in SORT_OPTIONS"
                      :key="opt.value"
                      class="dd-item"
                      :class="{
                        'is-active': sortState(column) === opt.value,
                      }"
                      @click="applySort(column, opt.value)"
                    >
                      <span class="icon">
                        <i :class="opt.icon" />
                      </span>
                      {{ opt.label }}
                    </a>
                  </template>
                </IconButton>
                {{ column.label }}
              </span>
              <IconButton
                v-if="filters[column.id]"
                icon="fa-solid fa-filter"
                tooltip="Remove filter"
                color="primary"
                size="small"
                colorful
                :show-arrow="false"
                @click="unsetFilter(column.id)"
              />
            </span>
          </th>
        </tr>
      </tfoot>
    </table>
    <div v-if="dragRectStyle" class="drag-select-rect" :style="dragRectStyle" />
  </div>
</template>

<script>
import { Sortable } from '@shopify/draggable'
import { computed, provide, ref, toRef } from 'vue'

import {
  SELECTION_MODES,
  useSelectableRecords,
} from '@/composables/useSelectableRecords'
import { useSignedDownload } from '@/composables/useSignedDownload'

import DataTableRow from '@/components/listings/DataTableRow.vue'
import SearchField from '@/components/SearchField.vue'
import StatusWidget from '@/components/StatusWidget.vue'
import HelpIcon from '@/components/ui/HelpIcon.vue'
const SORT_OPTIONS = [
  { value: 'asc', label: 'Ascending', icon: 'fa-solid fa-sort-amount-up' },
  { value: 'desc', label: 'Descending', icon: 'fa-solid fa-sort-amount-down' },
  { value: 'none', label: 'None', icon: 'fa-solid fa-ban' },
]

export default {
  components: {
    SearchField,
    StatusWidget,
    DataTableRow,
    HelpIcon,
  },
  props: {
    changeSort: {
      type: Function,
    },
    clearFilters: {
      type: Function,
    },
    columns: {
      type: Array,
    },
    records: {
      type: Array,
    },
    filters: {
      type: Object,
    },
    setFilter: {
      type: Function,
    },
    unsetFilter: {
      type: Function,
    },
    showFilters: {
      type: Boolean,
    },
    sort: {
      type: Object,
    },
    isLoading: {
      type: Boolean,
    },
    isRefreshing: {
      type: Boolean,
      default: false,
    },
    maxRecords: {
      type: Number,
    },
    selectionMode: {
      type: String,
      default: 'select',
      validator: (v) => SELECTION_MODES.includes(v),
    },
    appName: {
      type: String,
      default: '',
    },
    modelName: {
      type: String,
      default: '',
    },
  },
  emits: ['selected', 'toggle-filters', 'move-column', 'remove-column'],
  setup(props, { emit }) {
    provide('fieldPage', 'list')
    const { download: signedDownload } = useSignedDownload()
    const records = computed(() => props.records)
    const {
      selectables: selectableRecords,
      toggleSelect,
      allSelected,
      toggleSelectAll,
      setSelection,
      selectedIndices,
    } = useSelectableRecords(records, {
      mode: toRef(props, 'selectionMode'),
      emit,
    })

    const dragSelectAnchor = ref(null)
    const dragStartPos = ref(null)
    const dragCurrentPos = ref(null)
    const dragAdditive = ref(false)
    // Selection that existed before the drag started; the drag range is layered
    // on top of it so the live selection always mirrors the current range.
    const dragBaseIndices = ref([])

    const dragRectStyle = computed(() => {
      if (!dragStartPos.value || !dragCurrentPos.value) return null
      const left = Math.min(dragStartPos.value.x, dragCurrentPos.value.x)
      const top = Math.min(dragStartPos.value.y, dragCurrentPos.value.y)
      const width = Math.abs(dragCurrentPos.value.x - dragStartPos.value.x)
      const height = Math.abs(dragCurrentPos.value.y - dragStartPos.value.y)
      return {
        left: `${left}px`,
        top: `${top}px`,
        width: `${width}px`,
        height: `${height}px`,
      }
    })

    function onRowMousedown(index, event) {
      if (props.selectionMode === 'radio') return
      dragSelectAnchor.value = index
      dragAdditive.value = event.metaKey || event.ctrlKey
      // Additive drags keep the prior selection; plain drags replace it.
      dragBaseIndices.value = dragAdditive.value
        ? [...selectedIndices.value]
        : []
      dragStartPos.value = { x: event.clientX, y: event.clientY }
      dragCurrentPos.value = { x: event.clientX, y: event.clientY }
    }

    function onRowMouseenter(index) {
      if (dragSelectAnchor.value === null) return
      // Recompute from scratch each move so rows that leave the shrinking
      // range get deselected (drag both adds and removes).
      const min = Math.min(dragSelectAnchor.value, index)
      const max = Math.max(dragSelectAnchor.value, index)
      let finalIndices
      if (dragAdditive.value) {
        // Cmd/Ctrl-drag toggles each row in the range against the prior
        // selection: already-selected rows get deselected, and vice versa.
        const set = new Set(dragBaseIndices.value)
        for (let i = min; i <= max; i++) {
          if (set.has(i)) set.delete(i)
          else set.add(i)
        }
        finalIndices = [...set]
      } else {
        // Plain drag selects exactly the current range.
        const range = []
        for (let i = min; i <= max; i++) range.push(i)
        finalIndices = range
      }
      // Single mutation → one `'selected'` emit per move (no transient empty).
      setSelection(finalIndices)
    }

    function onBodyMousemove(event) {
      if (dragSelectAnchor.value === null) return
      dragCurrentPos.value = { x: event.clientX, y: event.clientY }
    }

    function onDragSelectEnd() {
      dragSelectAnchor.value = null
      dragStartPos.value = null
      dragCurrentPos.value = null
      dragAdditive.value = false
      dragBaseIndices.value = []
    }

    return {
      SORT_OPTIONS,
      selectableRecords,
      toggleSelect,
      toggleSelectAll,
      allSelected,
      dragSelectAnchor,
      dragRectStyle,
      onRowMousedown,
      onRowMouseenter,
      onBodyMousemove,
      onDragSelectEnd,
      signedDownload,
    }
  },
  data() {
    return {
      removingColumnIndex: -1,
      hoverFilterIcon: false,
      lastHoveredColIndex: -1,
    }
  },
  computed: {
    columnsIds() {
      return this.columns.map((c) => c.id)
    },
    displayDeletedItem() {
      const someDeleted = this.records
        ?.map((r) => r.deleted?.value === true)
        ?.some(Boolean)
      // Deleted items are shown either when the deleted filter is set to true
      // or when the filter is not set (all items are shown)
      return (
        someDeleted &&
        (this.filters.deleted?.value === 'true' || !this.filters.deleted)
      )
    },
    displayItemStatus() {
      return this.records
        ?.map((r) => r.sample_status || r.status || r.state)
        ?.some(Boolean)
    },
    displayDownloadLink() {
      return this.records
        ?.map((r) => r.__meta__.downloadable || r.__meta__.viewable)
        ?.some(Boolean)
    },
    showFooter() {
      return this.records?.length > 10
    },
    hasFilters() {
      return Object.keys(this.filters).length > 0
    },
  },
  watch: {
    isRefreshing(refreshing) {
      const ths = this.$refs.tableHeaderRow?.children
      if (!ths) return
      if (refreshing) {
        for (const th of ths) {
          th.style.minWidth = `${th.offsetWidth}px`
        }
      } else {
        // Double-rAF: first rAF fires before paint (new data renders with
        // locked widths), second rAF fires after that paint → release.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const current = this.$refs.tableHeaderRow?.children
            if (!current) return
            for (const th of current) {
              th.style.minWidth = ''
            }
          })
        })
      }
    },
    columnsIds: function (newColIds, oldColIds) {
      // auto scroll to newly added column
      newColIds.reverse()
      oldColIds.reverse()
      let id = null
      for (let colId of newColIds) {
        if (!oldColIds.includes(colId)) {
          id = colId
          break
        }
      }
      if (id) {
        this.$nextTick(() => {
          try {
            let scrollToElem = this.$refs[`head-${id}`][0]
            scrollToElem.scrollIntoView({
              behavior: 'smooth',
              inline: 'end',
            })
          } catch (e) {
            console.error(`Auto scroll failed ${e}`)
          }
        })
      }
    },
  },
  mounted: function () {
    this.tableHeader = this.$refs.tableHeaderRow
    this.sortable = new Sortable(this.tableHeader, {
      draggable: '.th--draggable',
      handle: '.th--handle',
    })
    this.sortable.on('sortable:stop', ({ oldIndex, newIndex }) => {
      // this.moveColumn({ fromIndex: oldIndex, toIndex: newIndex })
      this.$emit('move-column', { fromIndex: oldIndex, toIndex: newIndex })
    })
  },
  methods: {
    removeColumn(index) {
      this.removingColumnIndex = index
      return setTimeout(() => {
        this.$emit('remove-column', { index })
        this.$nextTick(() => {
          this.removingColumnIndex = -1
        })
      }, 150)
    },
    putColumnFirst(oldIndex) {
      this.$emit('move-column', { fromIndex: oldIndex, toIndex: 0 })
    },
    sortState(column) {
      if (column.id !== this.sort.columnId) return 'none'
      return this.sort.order || 'asc'
    },
    sortIcon(column) {
      const state = this.sortState(column)
      if (state === 'none') return 'fa-solid fa-sort-amount-down'
      const opt = SORT_OPTIONS.find((o) => o.value === state)
      return opt ? opt.icon : 'fa-solid fa-sort-amount-down'
    },
    applySort(column, value) {
      if (value === this.sortState(column)) return
      if (value === 'none') {
        this.changeSort({ columnId: null })
      } else {
        this.changeSort({ columnId: column.id, order: value })
      }
    },
    handleClearFilters() {
      if (this.hasFilters) {
        this.clearFilters()
      }
    },
    handleColumnHover(event) {
      // Find the td cell (event delegation)
      const cell = event.target.closest('td')
      if (!cell) return

      // Get column index within the row
      const colIndex = Array.from(cell.parentElement.children).indexOf(cell)
      if (colIndex === this.lastHoveredColIndex) return

      // Clear previous highlight (direct DOM manipulation, no Vue reactivity)
      this.clearColumnHover()

      // Find and highlight the corresponding header + filter cells
      const headerRow = this.$refs.tableHeaderRow
      const filterRow = this.$refs.tableFilterRow
      if (headerRow && headerRow.children[colIndex]) {
        headerRow.children[colIndex].classList.add('column-hovered')
        this.lastHoveredColIndex = colIndex
      }
      if (filterRow && filterRow.children[colIndex]) {
        filterRow.children[colIndex].classList.add('column-hovered')
      }
    },
    handleSignedDownload(id) {
      this.signedDownload(this.appName, this.modelName, id)
    },
    clearColumnHover() {
      if (this.lastHoveredColIndex >= 0) {
        const headerRow = this.$refs.tableHeaderRow
        const filterRow = this.$refs.tableFilterRow
        if (headerRow && headerRow.children[this.lastHoveredColIndex]) {
          headerRow.children[this.lastHoveredColIndex].classList.remove(
            'column-hovered',
          )
        }
        if (filterRow && filterRow.children[this.lastHoveredColIndex]) {
          filterRow.children[this.lastHoveredColIndex].classList.remove(
            'column-hovered',
          )
        }
        this.lastHoveredColIndex = -1
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// ===========================================
// TABLE WRAPPER - Root container
// ===========================================
.table-wrapper {
  max-height: 100%;
  position: relative;
  overflow: hidden;

  &.has-filters {
    tbody {
      height: 100%;
    }
  }

  // Column highlight when a filter is active — override row stripe/selection backgrounds
  :deep(td.has-filter-active),
  :deep(th.has-filter-active) {
    background-color: var(--bg-col-highlight);
  }
  :deep(tbody tr:nth-child(even):not(.is-selected) > td.has-filter-active) {
    background-color: var(--bg-col-highlight-stripe);
  }
  :deep(tbody tr.is-selected > td.has-filter-active) {
    background-color: var(--bg-col-highlight-selected);
  }
  :deep(tbody tr.is-selected:hover > td.has-filter-active) {
    background-color: var(--bg-col-highlight-selected-hover);
  }
  :deep(tbody tr:not(.is-selected):hover > td.has-filter-active) {
    background-color: var(--bg-col-highlight-hover);
  }
}

// ===========================================
// TABLE — reusable base + DataTable specifics
// ===========================================
.data-table {
  @include data-table;

  // DataTable-specific cell overrides
  td,
  th {
    &._checkbox {
      padding: var(--space-2) var(--space-3);
    }
  }
}

tbody.is-drag-selecting {
  user-select: none;
  cursor: crosshair;
}

.drag-select-rect {
  position: fixed;
  pointer-events: none;
  border: 2px dashed var(--border-strong);
  z-index: var(--z-dropdown);
}

.table-header {
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: var(--bg-stripe);

  // No border between header row and filter row
  tr:first-child th {
    border-bottom: none;
  }

  // Thick border between header block and table body
  tr:last-child th {
    border-bottom: 2px solid var(--border-default);
  }
}

.table-footer {
  position: sticky;
  bottom: 0;
  z-index: 1;
  background-color: var(--bg-stripe);

  tr:first-child th {
    border-top: 2px solid var(--border-default);
    border-bottom: none;
  }
}

.footer-cell {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-1);
}

.footer-cell__label {
  display: flex;
  align-items: center;
  gap: var(--space-1);
}

// ===========================================
// TABLE CELLS - td/th styling
// ===========================================
.cell-status {
  width: 30px;
  line-height: 0;
}

.table-cell {
  display: block;
  // Shrink to the value's width so the empty area of the cell stays
  // row-selectable; only the value itself is exempt from row-click and
  // shows the text cursor (see INTERACTIVE_SELECTOR in DataTableRow).
  width: fit-content;
  min-width: 50px;
  max-width: 350px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  // Let users select/copy the value text.
  user-select: text;
  cursor: text;

  // Apply ellipsis only to text content, not icons
  :deep(> *:not(.icon)) {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

// ===========================================
// HEADER CELLS - Column headers with interactions
// ===========================================
.table-wrapper th {
  font-weight: 600;
  position: relative;
}

.header-cell {
  &.has-filter-set {
    border-bottom: 3px solid var(--color-link);
  }
}

.header-cell__content {
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

.header-cell__label {
  white-space: nowrap;

  &.is-sorted {
    color: var(--primary, hsl(209, 62%, 50%));
    text-decoration: underline dotted;
    text-underline-offset: 3px;
  }
}

.header-cell__actions {
  display: flex;
  align-items: center;
  gap: 0.1rem;
  margin-left: auto;
  order: 2;
}

.header-cell__sort {
  visibility: hidden;

  &.is-active {
    visibility: visible;
  }
}

.header-cell:hover .header-cell__sort,
.column-hovered .header-cell__sort,
.header-cell__sort:has([data-state='open']) {
  visibility: visible;
}

// Action buttons: hidden by default, show on hover or column-hover
.header-cell__btn {
  visibility: hidden;

  &.is-active {
    visibility: visible;
  }
}

.header-cell:hover .header-cell__btn,
.column-hovered .header-cell__btn,
.header-cell__btn:has([data-state='open']) {
  visibility: visible;
}

// Grab cursor on the drag handle
.header-cell :deep(.th--handle) {
  cursor: grab;
}

// ===========================================
// ROWS - tr styling and states
// ===========================================
.table-wrapper tbody tr {
  height: 37px;

  td {
    &.removal-in-progress {
      opacity: 0.5;

      > div {
        max-width: 10px;
      }
    }
  }
}

// Deleted record rows
tr.is-deleted-record:not(.is-selected) {
  background-color: transparent;
  opacity: 0.6;

  &:hover {
    opacity: 1;
  }
}

.deleted-record-field {
  :deep(*:not(:hover)) {
    color: var(--danger);
    text-decoration: line-through;
  }
}

// ===========================================
// LOADING & EMPTY STATES
// ===========================================
.table-wrapper tbody {
  transition: opacity 0.15s ease;
}

.table-wrapper.refreshing tbody {
  opacity: 0.4;
  pointer-events: none;
}

.loading-body {
  height: 300px;
}

.no-data-body {
  background-color: whitesmoke;
  height: 200px;
}

.progress-row {
  height: 0;
  line-height: 0;
  font-size: 0;
}
.progress-cell {
  padding: 0 !important;
  border: none !important;
  height: 3px;
}

.skeleton-row {
  height: 43px !important;

  & > td {
    border-top-color: transparent !important;
    border-bottom-color: transparent !important;
  }
}

// ===========================================
// ICONS & VISIBILITY
// ===========================================
.icon-slash {
  font-size: 1.2rem;
}

// ===========================================
// UTILITIES
// ===========================================
.no-select {
  user-select: none;
}

// Dragging placeholder (the original element being dragged)
.header-cell.draggable-source--is-dragging {
  opacity: 0.3;
  background-color: var(--scheme-main-bis);
}
</style>

<style lang="scss">
// Unscoped — these rules must reach slotted td/th from parent components
// (e.g. more-data-columns slot) which scoped styles cannot target.
.data-table {
  // Base cell styles for slotted cells
  td,
  th {
    padding: var(--space-2) var(--space-4);
    height: 2em;
    max-height: 2em;
    vertical-align: middle;

    &.cell-centered {
      text-align: center;
    }

    &.cell-shrink,
    &.icon-column {
      width: 1px;
      white-space: nowrap;
      padding-inline: var(--space-1);
    }
  }

  tbody td {
    border-bottom: 1px solid var(--border-subtle);
  }

  tbody tr:last-child td {
    border-bottom-width: 0;
  }
}

// Unscoped — the drag mirror is appended to <body>
th.draggable-mirror {
  z-index: 40;
  background-color: var(--brand-light);
  border: 1px solid var(--brand);
  opacity: 0.9;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

body.draggable--is-dragging {
  cursor: grabbing !important;
}
</style>
